import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { DealTeamTriggerDialog } from '@viapeople/components'
import { useState } from 'react'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

type RouterOutput = inferRouterOutputs<AppRouter>
type CrmIntegration = RouterOutput['client']['dealTeamWorkflows']['listIntegrations'][number]

interface TriggerWorkflowDialogProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
	integration: CrmIntegration
}

export function TriggerWorkflowDialog({ isOpen, onClose, organizationId, integration }: TriggerWorkflowDialogProps) {
	const { showToast } = useToast()
	const queryClient = useQueryClient()
	const [userSearch, setUserSearch] = useState('')

	const { data: orgUsersData } = useQuery({
		...trpc.client.users.search.queryOptions({
			organizationId,
			search: userSearch || undefined,
			page: 1,
			perPage: 25,
			sortBy: 'firstName',
			order: 'asc',
			excludeCustomFields: true,
		}),
		enabled: isOpen,
	})
	const orgUsers = orgUsersData?.data ?? []

	const triggerManualMutation = useMutation(trpc.client.dealTeamWorkflows.triggerManualV2.mutationOptions())
	const triggerCsvMutation = useMutation(trpc.client.dealTeamWorkflows.triggerCsv.mutationOptions())

	const invalidateTriggerLogs = () => {
		queryClient.invalidateQueries({
			queryKey: trpc.client.dealTeamWorkflows.listTriggerLogs.queryKey({ organizationId }),
		})
	}

	return (
		<DealTeamTriggerDialog
			isOpen={isOpen}
			onClose={onClose}
			integration={integration}
			orgUsers={orgUsers}
			userSearch={userSearch}
			onUserSearchChange={setUserSearch}
			triggerManual={(input) =>
				triggerManualMutation.mutateAsync({
					organizationId,
					integrationId: integration.id,
					...input,
				})
			}
			triggerCsv={(input) =>
				triggerCsvMutation.mutateAsync({
					organizationId,
					integrationId: integration.id,
					...input,
				})
			}
			onShowToast={(msg, level) => showToast(msg, level === 'error' ? 'error' : undefined)}
			onTriggerSuccess={invalidateTriggerLogs}
		/>
	)
}
